/**
 * The map sheet's lettering and chrome, as a shell `DrawOp` list: the panel
 * header, the well and the key board, the grid's printed row/column labels,
 * the aerodrome index beside each ring, and the marginal key down the right.
 *
 * Everything here goes through `shell/render/`'s existing primitives — the
 * same `panelOps` header the Realism Panel and every briefing screen wear, the
 * same `sunkenWellOps`/`raisedPlateOps` bevels, the same `FONT3` bitmap type
 * and `embossOn` ink — so the map reads as a screen the original could have
 * had rather than as a modern overlay wearing a sepia filter. The one thing
 * the shell has no primitive for is a line at an angle, and that is the whole
 * of `map-paint.ts`.
 *
 * Pure: ops in logical 320×200 coordinates with palette indices, asserted in a
 * test without a canvas, exactly as `draw-ops.ts` intends.
 */

import {
  fill,
  type DrawOp,
  type FontId,
  type Rect,
  type TextStyle,
} from "../shell/render/draw-ops.ts";
import { panelOps } from "../shell/render/panel-chrome.ts";
import { embossOn, PLATE_FILL, raisedPlateOps, sunkenWellOps } from "../shell/render/plate-chrome.ts";
import { measureText, type FontAtlas } from "../shell/render/text-metrics.ts";
import {
  FIELD_INK,
  KEY_BOARD,
  KEY_INSIDE,
  KEY_ROW_PITCH,
  MAP_PAPER,
  MAP_WELL,
  PAPER,
  PENCIL_INK,
  ROAD_INK,
  SEA_INK,
  WATER_INK,
  WOOD_INK,
  mapProjection,
} from "./map-layout.ts";
import { nearestAerodrome, wldPointOf, type MapModel, type MapPlayer } from "./map-model.ts";
import { METRES_PER_MILE } from "../flight/mission-hud.ts";
import {
  cellIndexRange,
  digitCellCentreX,
  letterCellCentreY,
  sectorCodeAt,
  sectorDigit,
  sectorLetter,
  SECTOR_CELL_MILES,
  SECTOR_PREFIX,
} from "./sector-code.ts";
import { WLD_UNITS_PER_METRE } from "../world/wld-units.ts";

const LABEL_FONT: FontId = "FONT3";
/** Flat, unembossed ink for anything printed on the paper — the paper is not a raised plate, and the original's flat text (the `MAIN MENU` banner) is written exactly this way. */
const PAPER_TEXT: TextStyle = { body: PENCIL_INK };
const KEY_TEXT: TextStyle = embossOn(PLATE_FILL);
/** Two indices darker in the body than a plain row, which is how the shell's own lists separate a heading from what it heads (`plate-chrome.ts`'s `LIST_TEXT`/`LIST_SELECTED_TEXT` pair). */
const KEY_HEADING: TextStyle = { body: 238, shadow: 241, highlight: 226 };

/**
 * The panel title, in the original's own phrasing. `PS.EXE`'s briefing
 * template reads `REFER TO THE MAP OF THE` and the manual's worked example
 * completes it — *"Refer to the map of the Verdun Region to locate this
 * position"* (`docs/game/manual/04-gameplay.md:345`).
 */
export function mapTitle(model: MapModel): string {
  return `MAP OF THE ${model.regionName.toUpperCase()} REGION`;
}

function text(font: FontId, value: string, x: number, y: number, style: TextStyle): DrawOp {
  return { kind: "text", font, text: value, x, y, style };
}

/** Trims a label to what will fit in `width` pixels of `font` — the key board is 109px wide and `Le Plessis Belleville` is not. */
function fitted(font: FontAtlas, value: string, width: number): string {
  if (measureText(font, value) <= width) return value;
  let trimmed = value;
  while (trimmed.length > 1 && measureText(font, `${trimmed}.`) > width) trimmed = trimmed.slice(0, -1);
  return `${trimmed}.`;
}

/**
 * The printed grid references: letters down the paper's left edge, digits
 * along its top, each centred on its own cell. This is the original's own
 * orientation device — the thing a briefing means by "spotted: sector E-6" —
 * and it is drawn from the same two constants the executable computes a code
 * with, so a cell here is that cell.
 */
function gridLabelOps(model: MapModel): DrawOp[] {
  const projection = mapProjection(model.bounds);
  const min = wldPointOf(model, { u: model.bounds.minU, v: model.bounds.minV });
  const max = wldPointOf(model, { u: model.bounds.maxU, v: model.bounds.maxV });
  const corners = [sectorCodeAt(min), sectorCodeAt(max)];
  const ops: DrawOp[] = [];

  for (const index of cellIndexRange(corners[0]?.letterIndex ?? 0, corners[1]?.letterIndex ?? 0)) {
    const v = letterCellCentreY(index) / WLD_UNITS_PER_METRE - model.origin.z;
    const at = projection.toScreen({ x: model.bounds.minU, y: v });
    if (at.y < 4 || at.y > MAP_PAPER.h - 8) continue;
    ops.push(text(LABEL_FONT, sectorLetter(index), MAP_PAPER.x + 1, MAP_PAPER.y + at.y - 4, PAPER_TEXT));
  }
  for (const index of cellIndexRange(corners[0]?.digitIndex ?? 0, corners[1]?.digitIndex ?? 0)) {
    const u = digitCellCentreX(index) / WLD_UNITS_PER_METRE - model.origin.x;
    const at = projection.toScreen({ x: u, y: model.bounds.maxV });
    if (at.x < 4 || at.x > MAP_PAPER.w - 6) continue;
    ops.push(text(LABEL_FONT, sectorDigit(index), MAP_PAPER.x + at.x - 2, MAP_PAPER.y + 1, PAPER_TEXT));
  }
  return ops;
}

/** The index beside each aerodrome's ring, keying it to the list on the board. */
function aerodromeIndexOps(model: MapModel): DrawOp[] {
  const projection = mapProjection(model.bounds);
  return model.aerodromes.map((aerodrome, index) => {
    const at = projection.toScreen({ x: aerodrome.u, y: aerodrome.v });
    return text(LABEL_FONT, String(index + 1), MAP_PAPER.x + at.x + 4, MAP_PAPER.y + at.y - 4, PAPER_TEXT);
  });
}

/**
 * Which way is up. The `.WLD` axes' true bearing is still UNKNOWN
 * (`world/placement-transform.ts` has never resolved it), so this is not a
 * decoded fact — it is the port's *own* compass convention made visible: the
 * HUD already prints heading 0 as `N` (`flight/hud-format.ts`'s
 * `headingCardinal`), and heading 0 points up this sheet, so the two agree.
 * Drawn as a plain `N` over a stem rather than a rose, which is all a 5-pixel
 * corner of a 192-pixel map can carry.
 */
function compassOps(font: FontAtlas): DrawOp[] {
  const x = MAP_PAPER.x + MAP_PAPER.w - 9;
  const y = MAP_PAPER.y + 2;
  const stem = x + Math.floor(measureText(font, "N") / 2);
  return [
    // The needle hangs below the letter: a 7-row stem with a two-step barb at
    // its top, which is the widest an arrowhead can be and still read at 1:1.
    text(LABEL_FONT, "N", x, y, PAPER_TEXT),
    fill({ x: stem, y: y + 9, w: 1, h: 8 }, PENCIL_INK),
    fill({ x: stem - 1, y: y + 10, w: 1, h: 1 }, PENCIL_INK),
    fill({ x: stem + 1, y: y + 10, w: 1, h: 1 }, PENCIL_INK),
    fill({ x: stem - 2, y: y + 11, w: 1, h: 1 }, PENCIL_INK),
    fill({ x: stem + 2, y: y + 11, w: 1, h: 1 }, PENCIL_INK),
  ];
}

/** `"SECTOR E6"`, or the withheld form REAL NAVIGATION leaves in its place. */
export function sectorReadout(model: MapModel, player: MapPlayer | undefined, hidePosition: boolean): string {
  if (hidePosition) return `${SECTOR_PREFIX}- REAL NAV`;
  if (!player) return `${SECTOR_PREFIX}--`;
  return SECTOR_PREFIX + sectorCodeAt(wldPointOf(model, player)).code;
}

/** Where the player is, in the terms a pilot actually uses: how far from which field. */
function bearingRow(model: MapModel, player: MapPlayer | undefined, hidePosition: boolean): string {
  if (hidePosition || !player) return "";
  const nearest = nearestAerodrome(model, player);
  if (!nearest) return "";
  const miles = nearest.distanceM / METRES_PER_MILE;
  // Distance first and no "FROM": the board is 109 pixels wide and
  // `Le Plessis Belleville` is 21 characters, so every word spent on grammar is
  // a word `fitted` has to take back off the name.
  return `${miles < 10 ? miles.toFixed(1) : String(Math.round(miles))} MI ${nearest.marker.label.toUpperCase()}`;
}

/**
 * One entry of the marginal ink key: a colour swatch and the feature class it
 * names.
 *
 * The swatch is drawn as a scrap of the sheet's own paper with the class's ink
 * on it, not as a bare block of colour, because two of the four are deliberately
 * faint and a faint ink shown against the key board's tan reads as nothing at
 * all. Against `PAPER` it reads as exactly what it looks like on the map, which
 * is the only thing a legend is for. `line` is the second ink of a class printed
 * in two — water washes pale and draws its rivers and shore dark.
 */
export interface InkKeyEntry {
  readonly label: string;
  readonly ink: number;
  readonly line?: number;
}

/**
 * The ink key, two entries to a row because the board has room for the width
 * and none at all for the height: a nine-aerodrome region (Verdun, Somme)
 * fills the board to its last row exactly.
 *
 * The four classes here are the ones the terrain data distinguishes
 * (`map-features.ts`). The marks that are *not* printed geography — the front,
 * the target, the hill, the aeroplane — keep their own rows below, as they
 * always had.
 */
const INK_KEY_ROWS: readonly (readonly InkKeyEntry[])[] = [
  [
    { label: "WATER", ink: SEA_INK, line: WATER_INK },
    { label: "ROAD", ink: ROAD_INK },
  ],
  [
    { label: "WOOD", ink: WOOD_INK },
    { label: "FIELD", ink: FIELD_INK },
  ],
];

/** Swatch size, and where the key's two columns start relative to `KEY_INSIDE`. */
export const SWATCH_W = 9;
export const SWATCH_H = 7;
const INK_KEY_COLUMN_DX: readonly number[] = [0, 55];
/** The gap between a swatch and the word it labels. */
const SWATCH_GAP = 3;

/** A row of the key board: either a line of text, or a row of the ink key. */
type KeyRow = { readonly text: string; readonly heading?: boolean } | { readonly ink: readonly InkKeyEntry[] };

/**
 * The key board's rows, top to bottom. Nineteen rows of `KEY_ROW_PITCH` is
 * what `KEY_INSIDE` holds, and the content is sized to it exactly: two readout
 * rows, a heading, up to nine aerodromes, two rows of ink key, and four rows
 * of legend and labelling. Blank rows are dropped rather than reserved, so
 * REAL NAVIGATION (which empties the bearing row) simply closes the gap.
 */
function keyRows(model: MapModel, player: MapPlayer | undefined, hidePosition: boolean): KeyRow[] {
  const rows: KeyRow[] = [
    // The grid's own scale, and it is a recovered number rather than a chosen
    // one: `RB.EXE`'s sector divisor is 253 440 `.WLD` units, which is 31 680
    // feet to the foot — six statute miles (`sector-code.ts`).
    { text: `1 SQUARE = ${SECTOR_CELL_MILES} MILES`, heading: true },
    { text: sectorReadout(model, player, hidePosition) },
    { text: bearingRow(model, player, hidePosition) },
    { text: "AERODROMES", heading: true },
    ...model.aerodromes.map((aerodrome, index) => ({ text: `${index + 1} ${aerodrome.label.toUpperCase()}` })),
    ...INK_KEY_ROWS.map((ink) => ({ ink })),
    { text: "= = FRONT  O HILL" },
    { text: hidePosition ? "+ TARGET  NO FIX" : "+ TARGET  * PLANE" },
    { text: "M CLOSES THIS MAP" },
    { text: "PORT EXTENSION" },
  ];
  return rows.filter((row) => "ink" in row || row.text !== "");
}

/** Where a row sits, in the shell's logical frame. Rows past the board's bottom edge are not drawn. */
function rowTop(index: number): number | undefined {
  const y = KEY_INSIDE.y + index * KEY_ROW_PITCH;
  return y + 8 > KEY_INSIDE.y + KEY_INSIDE.h ? undefined : y;
}

/**
 * The ink key's swatches, in logical coordinates, for the compositor to blit.
 *
 * They cannot be `fill` ops: two of the four inks are `map-raster.ts`'s
 * synthetic indices, which live past `PS.PAL`'s 256 and only that module knows
 * how to resolve. Handing back rectangles keeps the shell's painter free of any
 * knowledge of them — `MapOverlay.tsx` blits each through the same
 * `rasterToImageData` the paper itself goes through, so a swatch is guaranteed
 * to be the colour the map draws that class in.
 */
export function inkKeySwatches(model: MapModel, player: MapPlayer | undefined, hidePosition: boolean): { readonly rect: Rect; readonly entry: InkKeyEntry }[] {
  const swatches: { rect: Rect; entry: InkKeyEntry }[] = [];
  keyRows(model, player, hidePosition).forEach((row, index) => {
    if (!("ink" in row)) return;
    const y = rowTop(index);
    if (y === undefined) return;
    row.ink.forEach((entry, column) => {
      swatches.push({ rect: { x: KEY_INSIDE.x + (INK_KEY_COLUMN_DX[column] ?? 0), y: y + 1, w: SWATCH_W, h: SWATCH_H }, entry });
    });
  });
  return swatches;
}

function keyBoardOps(model: MapModel, player: MapPlayer | undefined, hidePosition: boolean, font: FontAtlas): DrawOp[] {
  const ops: DrawOp[] = [...raisedPlateOps(KEY_BOARD)];
  keyRows(model, player, hidePosition).forEach((row, index) => {
    const y = rowTop(index);
    if (y === undefined) return;
    if ("ink" in row) {
      row.ink.forEach((entry, column) => {
        const x = KEY_INSIDE.x + (INK_KEY_COLUMN_DX[column] ?? 0) + SWATCH_W + SWATCH_GAP;
        ops.push(text(LABEL_FONT, entry.label, x, y, KEY_TEXT));
      });
      return;
    }
    ops.push(text(LABEL_FONT, fitted(font, row.text, KEY_INSIDE.w), KEY_INSIDE.x, y, row.heading ? KEY_HEADING : KEY_TEXT));
  });
  return ops;
}

/** The rectangle `map-paint.ts`'s raster is blitted into — exported so the painter and the compositor cannot disagree about it. */
export const PAPER_RECT: Rect = MAP_PAPER;

/**
 * Every op the map screen draws *except* the paper's own raster, which goes in
 * between: the caller paints these, blits the raster into `PAPER_RECT`, then
 * paints `mapOverlayOps` on top so the lettering sits over the ink.
 */
export function mapChromeOps(model: MapModel, titleFont: FontAtlas): DrawOp[] {
  return [
    ...panelOps(mapTitle(model), "none", titleFont),
    ...sunkenWellOps(MAP_WELL, PAPER),
  ];
}

/** The lettering that belongs over the paper's ink, plus the key board beside it. */
export function mapOverlayOps(
  model: MapModel,
  player: MapPlayer | undefined,
  hidePosition: boolean,
  font: FontAtlas,
): DrawOp[] {
  return [
    ...gridLabelOps(model),
    ...aerodromeIndexOps(model),
    ...compassOps(font),
    ...keyBoardOps(model, player, hidePosition, font),
  ];
}
